import { Finding, SuburbGraph, runChecks } from './suburb-map-checks';

/**
 * Sub-region edits on the suburb map (#260; plan §6.5), as pure functions so the rules are tested apart from the map.
 * They mirror the server's planner (conductor-server suburb-area-plan.ts planEdits), which has the last word: the map
 * applies these locally at once, then mirrors back the rows the server stored.
 *
 *   join    a suburb in the sub-region's region joins it. An unassigned one is put in that region first (the server
 *           derives the assignment from the one 'subregion' edit, in the same batch). One in another region is left
 *           alone and named: it has to be moved to the region first. So is an unassigned one whose page is in another
 *           region.
 *   leave   a member leaves; its region stays.
 *   region  moving or unassigning a suburb drops its memberships first (the server does the same, in the same batch).
 *   assign  Assign mode with a sub-region picked: the region change (if any) and the join, per suburb, in one batch.
 */

/** A suburb's state on the map: its region, whether it is covered, and the sub-regions it is in. */
export interface AreaState {
  regionId: string;
  covered: boolean;
  subRegions: string[];
}

export interface SubRegionRef {
  id: string;
  name: string;
  regionId: string;
}

export interface AreaEdit {
  salCode: string;
  field: 'region' | 'covered' | 'subregion';
  from: string | null;
  to: string | null;
  subRegionId?: string;
}

export type SubRegionAction = 'add' | 'remove';

export interface SubRegionEditPlan {
  /** One 'subregion' edit per suburb that changes, in the order given. */
  edits: AreaEdit[];
  /** Unassigned suburbs that go into the sub-region's region first. */
  assigned: string[];
  /** Suburbs left alone because they are in another region, with that region's id. */
  otherRegion: Array<{ code: string; regionId: string }>;
  /** Unassigned suburbs left alone because their page is in another region (the page's label). */
  pages: string[];
}

/**
 * The edits a click or a sweep makes to these suburbs for one sub-region. A click toggles membership; a sweep (paint,
 * lasso, polygon, grow) follows the action: Add joins the ones that aren't members, Remove takes out the ones that are.
 */
export function subRegionEdits(
  codes: string[],
  areas: ReadonlyMap<string, AreaState>,
  sr: SubRegionRef,
  action: SubRegionAction,
  gesture: 'click' | 'sweep',
  pages: ReadonlyMap<string, { regionId: string | null; label: string }> = new Map()
): SubRegionEditPlan {
  const plan: SubRegionEditPlan = { edits: [], assigned: [], otherRegion: [], pages: [] };
  const seen = new Set<string>();
  for (const code of codes) {
    if (seen.has(code)) continue;
    seen.add(code);
    const row = areas.get(code);
    const member = !!row?.subRegions.includes(sr.id);
    const join = gesture === 'click' ? !member : action === 'add';
    if (!join) {
      if (member)
        plan.edits.push({
          salCode: code,
          field: 'subregion',
          subRegionId: sr.id,
          from: '1',
          to: '0',
        });
      continue;
    }
    if (member) continue;
    if (row && row.regionId !== sr.regionId) {
      plan.otherRegion.push({ code, regionId: row.regionId });
      continue;
    }
    if (!row) {
      const page = pages.get(code);
      if (page && page.regionId !== sr.regionId) {
        plan.pages.push(page.label);
        continue;
      }
      plan.assigned.push(code);
    }
    plan.edits.push({ salCode: code, field: 'subregion', subRegionId: sr.id, from: '0', to: '1' });
  }
  return plan;
}

export interface AssignEditPlan {
  /** Per suburb, in the order given: its region change (if any), then its sub-region join (if any). */
  edits: AreaEdit[];
  /** Suburbs left alone because their page is in another region (the page's label). */
  kept: string[];
}

/**
 * Assign mode's edits (#260, Ivan 2026-10-21): put these suburbs in `regionId` and, when a sub-region of that region is
 * picked, in it too. Every gesture only adds (a click on a suburb already there does nothing). A suburb of another
 * region is moved, which drops its old memberships (the server does that, in the same batch), then joins. One whose
 * page is in another region is left alone, as without a sub-region. With no sub-region (null) this is Assign as it was.
 * A sub-region of another region is ignored: the pick belongs to the active region.
 */
export function assignEdits(
  codes: string[],
  areas: ReadonlyMap<string, AreaState>,
  regionId: string,
  sr: SubRegionRef | null,
  pages: ReadonlyMap<string, { regionId: string | null; label: string }> = new Map()
): AssignEditPlan {
  const plan: AssignEditPlan = { edits: [], kept: [] };
  const sub = sr && sr.regionId === regionId ? sr : null;
  const seen = new Set<string>();
  for (const code of codes) {
    if (seen.has(code)) continue;
    seen.add(code);
    const row = areas.get(code);
    const cur = row?.regionId ?? null;
    if (cur !== regionId) {
      const page = pages.get(code);
      if (page && page.regionId !== regionId) {
        plan.kept.push(page.label);
        continue;
      }
      plan.edits.push({ salCode: code, field: 'region', from: cur, to: regionId });
    } else if (!sub || row!.subRegions.includes(sub.id)) {
      continue;
    }
    if (sub)
      plan.edits.push({
        salCode: code,
        field: 'subregion',
        subRegionId: sub.id,
        from: '0',
        to: '1',
      });
  }
  return plan;
}

/**
 * Apply one edit to the map's local copy, as the server would store it. `regionOf` gives each sub-region's region (an
 * unassigned suburb joining one goes there first).
 */
export function applyAreaEdit(
  areas: Map<string, AreaState>,
  e: AreaEdit,
  regionOf: (subRegionId: string) => string | undefined
): void {
  const row = areas.get(e.salCode);
  if (e.field === 'region') {
    // A new region (or none) drops every membership: sub-regions belong to one region.
    if (e.to === null) areas.delete(e.salCode);
    else if (!row || row.regionId !== e.to)
      areas.set(e.salCode, { regionId: e.to, covered: false, subRegions: [] });
  } else if (e.field === 'covered') {
    if (row) row.covered = e.to === '1';
  } else if (e.subRegionId) {
    if (e.to === '1') {
      const regionId = row?.regionId ?? regionOf(e.subRegionId);
      if (!regionId) return;
      const next = row ?? { regionId, covered: false, subRegions: [] };
      if (!next.subRegions.includes(e.subRegionId))
        next.subRegions = [...next.subRegions, e.subRegionId].sort();
      areas.set(e.salCode, next);
    } else if (row) {
      row.subRegions = row.subRegions.filter((id) => id !== e.subRegionId);
    }
  }
}

/** Members of each sub-region, from the local copy. */
export function membersBySubRegion(areas: ReadonlyMap<string, AreaState>): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const [code, a] of areas)
    for (const id of a.subRegions) (out.get(id) ?? out.set(id, []).get(id)!).push(code);
  return out;
}

/**
 * Grow a sub-region: the suburbs of its region that border a member and aren't members yet, limited to `allowed` (the
 * suburbs drawn in the loaded state). Unassigned neighbours are left out: growing never moves the region's edge.
 */
export function growSubRegion(
  graph: SuburbGraph,
  areas: ReadonlyMap<string, AreaState>,
  sr: SubRegionRef,
  allowed: Set<string>
): string[] {
  const frontier = new Set<string>();
  for (const [code, a] of areas) {
    if (!a.subRegions.includes(sr.id)) continue;
    for (const nb of graph.edges[code] ?? []) {
      const n = areas.get(nb);
      if (n && n.regionId === sr.regionId && !n.subRegions.includes(sr.id) && allowed.has(nb))
        frontier.add(nb);
    }
  }
  return [...frontier];
}

export type SubRegionFinding =
  | { kind: 'enclave'; subRegionId: string; code: string }
  | { kind: 'island' | 'offshore'; subRegionId: string; code: string; size: number };

/**
 * Checks for each sub-region, on a map of its members only: split (members in pieces that border other land; a piece
 * with no land border at all is offshore, for information) and enclave (a suburb of the same region that every
 * neighbour of is a member: usually a miss). No cross-state check: a sub-region may cross a state line with its region
 * (Tweed Valley is NSW suburbs under Brisbane).
 */
export function subRegionChecks(
  graph: SuburbGraph,
  areas: ReadonlyMap<string, AreaState>,
  subRegions: SubRegionRef[]
): SubRegionFinding[] {
  const members = membersBySubRegion(areas);
  const out: SubRegionFinding[] = [];
  for (const sr of subRegions) {
    const codes = members.get(sr.id) ?? [];
    if (!codes.length) continue;
    const regionOf = new Map(codes.map((c) => [c, sr.id]));
    for (const f of runChecks(graph, regionOf, new Map()) as Finding[]) {
      if (f.kind === 'enclave') {
        if (areas.get(f.code)?.regionId === sr.regionId)
          out.push({ kind: 'enclave', subRegionId: sr.id, code: f.code });
      } else if (f.kind === 'island' || f.kind === 'offshore') {
        out.push({ kind: f.kind, subRegionId: sr.id, code: f.code, size: f.size });
      }
    }
  }
  return out;
}
